import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const RECENT_KEY = 'recently_viewed';
const CONTINUE_KEY = 'anime_continue_watching';

export interface RecentItem {
  id: number;
  title: string;
  poster: string;
  type: string;
  timestamp: number;
}

export interface ContinueWatchItem {
  id: number;
  title: string;
  poster: string;
  type: string;
  episode: number;
  timestamp: number;
  duration?: number;
  progress?: number;
}

export function getRecentlyViewed(): RecentItem[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

export function clearRecentlyViewed(): void {
  try { localStorage.removeItem(RECENT_KEY); } catch {}
}

export function addRecentlyViewed(item: Omit<RecentItem, 'timestamp'>): void {
  const items = getRecentlyViewed().filter(i => i.id !== item.id);
  items.unshift({ ...item, timestamp: Date.now() });
  localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 30)));
}

export function getContinueWatching(): ContinueWatchItem[] {
  try {
    return JSON.parse(localStorage.getItem(CONTINUE_KEY) || '[]');
  } catch { return []; }
}

export function addContinueWatching(item: Omit<ContinueWatchItem, 'timestamp'>): void {
  const items = getContinueWatching().filter(i => !(i.id === item.id && i.episode === item.episode));
  items.unshift({ ...item, timestamp: Date.now() });
  localStorage.setItem(CONTINUE_KEY, JSON.stringify(items.slice(0, 20)));
}

export function removeFromContinueWatching(id: number, episode?: number): void {
  const items = getContinueWatching().filter(i =>
    episode !== undefined
      ? !(i.id === id && i.episode === episode)
      : i.id !== id
  );
  localStorage.setItem(CONTINUE_KEY, JSON.stringify(items));
}

export function clearContinueWatching(): void {
  try { localStorage.removeItem(CONTINUE_KEY); } catch {}
}

const SCROLL_KEY = 'scroll_positions';

export function saveScrollPosition(path: string): void {
  try {
    const data = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    data[path] = window.scrollY;
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(data));
  } catch {}
}

export function getScrollPosition(path: string): number {
  try {
    const data = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    return data[path] || 0;
  } catch { return 0; }
}

export function useScrollRestore(enabled: boolean): void {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;
    const saved = getScrollPosition(location.pathname);
    if (saved > 0) requestAnimationFrame(() => window.scrollTo(0, saved));
  }, [location.pathname, enabled]);

  useEffect(() => {
    if (!enabled) return;
    saveScrollPosition(location.pathname);
    const handle = () => saveScrollPosition(location.pathname);
    window.addEventListener('beforeunload', handle);
    return () => {
      saveScrollPosition(location.pathname);
      window.removeEventListener('beforeunload', handle);
    };
  }, [location.pathname, enabled]);
}
